import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Icon, IconButton, Skeleton, useDisclosure} from '@chakra-ui/react';
import ProductInSharedExperienceListItem from './productInSharedExperienceListItem';
import {Box, Flex, Text, Stack} from '@chakra-ui/layout';
import {MdAdd, MdArrowBack, MdDevices} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { SharedExperiencesContext } from '../../lib/sharedExperiencesProvider';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal';
import AddProductToSharedExperienceForm from './addProductToSharedExperienceForm';
import ProductForm from '@/products/components/productForm';
import ProductProvider from '@/products/lib/productProvider';
import { SharedExperiencePageContext } from '../lib/sharedExperiencePageContext';
import useProductsApi from '@/products/lib/useProductsApi';

export default function ProductInSharedExperiencesList(props) {
  const {
    rights,
  } = useContext(SharedExperiencesContext);
  const {
    refreshProductList,
    refreshExperienceList,
  } = useContext(SharedExperiencePageContext);
  const productsApi = useProductsApi();
  const {sharedExperienceId} = props;
  const [isAdding, setIsAdding] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const navigate = useNavigate();
  const modalAddProduct = useDisclosure();
  const modalCreateProduct = useDisclosure();
  const ref1 = useRef(null);
  const ref2 = useRef(null);

  const loadLinkedProducts = async () => {
    try {
      setIsFetchingProducts(true);
      const productsData = await productsApi.fetchManyProductsBySharedExperienceId(sharedExperienceId);
      setLinkedProducts(productsData || []);
      setIsFetchingProducts(false);
    } catch (error) {
      console.error('Error loading linked products:', error);
      setIsFetchingProducts(false);
    }
  };

  useEffect(() => {
    if (sharedExperienceId) {
      loadLinkedProducts();
    }
  }, [sharedExperienceId]);

  const linkNewProductToSharedExperience = async (newProduct) => {
    try {
      await sharedExperiencesApi.linkProductToSharedExperience(sharedExperienceId, newProduct.id)
      await loadLinkedProducts();
      setIsAdding(false);
      modalCreateProduct.onClose();
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <>
      <Box w="100%">
        <Flex 
        alignItems='center'
        mb={2}
        justifyContent={"space-between"}
        px={3}
        >
          <Flex
            alignItems='center'
          >
            <Icon
              as={MdDevices}
              color={'text.primary'}
              boxSize={'14px'}
              mr={2}
            />
            <Text fontSize={14} >Products</Text>
          </Flex>
          {rights.can_update && (
            <IconButton
              ml={2}
              variant="greenOutline"
              icon={<MdAdd />}
              size="sm"
              onClick={modalAddProduct.onOpen}
            />
          )}
        </Flex>
        <Box maxH={300} overflowY={'auto'}>
          { isFetchingProducts && (
            <Stack p={3} spacing={2} backgroundColor={'white'}>
               <Skeleton height='30px' />
               <Skeleton height='30px' />
               <Skeleton height='30px' />
             </Stack>
          )}
          { (!isFetchingProducts && linkedProducts && linkedProducts.length === 0) && (
              <Box px={3}>
                <Text color="text.light.grey">No product so far.</Text>
              </Box>
          )}
           { (!isFetchingProducts && linkedProducts) && linkedProducts.map((p, i) => {
            return (
              <ProductProvider
                productId={p.id}
                orignalProduct={p}
                needRights={false}
                needRole={false}
                key={i}>
                  <Flex
                    style={{'cursor' : 'pointer'}}
                    onClick={() => {
                      navigate( `/products/${p.id}`)
                    }}
                  >
                    <ProductInSharedExperienceListItem
                      sharedExperienceId={sharedExperienceId}
                      onUpdate={() => {
                        loadLinkedProducts();
                        refreshExperienceList();
                      }}
                    />
                </Flex>
              </ProductProvider>
            );
          })}
        </Box>
      </Box>
      <Modal isOpen={modalAddProduct.isOpen} onClose={modalAddProduct.onClose}>
        <ModalOverlay  />
        <ModalContent >
          <ModalHeader>Add Product to Shared Experience</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <AddProductToSharedExperienceForm
                sharedExperienceId={sharedExperienceId}
                hideSave={true}
                onCreateNew={() => {
                  modalAddProduct.onClose();
                  modalCreateProduct.onOpen();
                }}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={() => {
                  setIsAdding(false);
                  modalAddProduct.onClose();
                  loadLinkedProducts();
                }}
                ref={ref1}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={modalAddProduct.onClose}
            >Close</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              onClick={() => {
                ref1.current.click();
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={modalCreateProduct.isOpen} onClose={modalCreateProduct.onClose}>
        <ModalOverlay  />
        <ModalContent >
          <ModalHeader>
            <IconButton
              fontSize='24'
              variant="tertiary"
              icon={<MdArrowBack/>}
              aria-label='New'
              mr={2}
              onClick={() => {
                modalCreateProduct.onClose();
                modalAddProduct.onOpen();
              }}
            />
            New Product</ModalHeader>
          <ModalBody >
            <Box p={3}>
              <ProductForm
                hideSave={true}
                onSubmitting={() => {
                  setIsAdding(true);
                }}
                onSubmitted={(newProduct) => {
                  linkNewProductToSharedExperience(newProduct)
                }}
                ref={ref2}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='greenOutline'
              onClick={() => {
                modalCreateProduct.onClose();
                modalAddProduct.onOpen();
              }}
            >Close</Button>
            <Button
              variant="greenFill"
              ml={2}
              isLoading={isAdding}
              onClick={() => {
                ref2.current.click();
              }}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
} 